import { css, html, nothing } from 'lit';
import { connect } from 'pwa-helpers';
import { customElement, property, state } from 'lit/decorators.js';
import { getLoginURLWithRedirectUrlToCurrentPage } from '../../common/utils/login-utils';
import store from '../../store/store';
import { StoreState } from '../../store/store.interface';
import { BaseComponent } from '../base-component/base-component';
import { isSuccessUser, isUserInstructor, isUserVerifiedInstructor } from '../user-profile/user-profile.selectors';
import { showConfirmInstructorModal } from './utils';

@customElement('mhecomm-download-button')
export class DownloadButton extends connect(store)(BaseComponent) {
  private static readonly PENDING_DOWNLOAD_STORAGE_KEY = 'mhecomm-download-button:pending-download-url';

  static styles = [
    css`
      .download-link {
        display: inline-block;
      }

      .download-link ::slotted(a) {
        cursor: pointer;
      }
    `,
  ];

  @property()
  downloadUrl = '';

  @property()
  label = 'Download';

  @state()
  private isVerifiedInstructor = false;

  @state()
  private isAuthenticatedUser = false;

  @state()
  private isInstructor = false;

  @state()
  private hasResolvedInstructorStatus = false;

  private hasHandledPendingDownload = false;

  private getSlottedAnchors(): HTMLAnchorElement[] {
    const slot = this.shadowRoot?.querySelector('slot');
    if (!slot) {
      return [];
    }

    return slot
      .assignedElements({ flatten: true })
      .filter((element): element is HTMLAnchorElement => element instanceof HTMLAnchorElement);
  }

  private syncSlottedAnchors() {
    const anchors = this.getSlottedAnchors();
    const downloadUrl = this.getDownloadUrl();

    for (const anchor of anchors) {
      if (downloadUrl) {
        anchor.href = downloadUrl;
      }
    }
  }

  stateChanged(state: StoreState) {
    const user = state.userProfileState.userProfileResponse.data;
    this.isAuthenticatedUser = isSuccessUser(state);
    this.isInstructor = this.isAuthenticatedUser && isUserInstructor(state);
    this.isVerifiedInstructor = this.isAuthenticatedUser && isUserVerifiedInstructor(state);
    this.hasResolvedInstructorStatus = !this.isInstructor || Boolean(user?.status);
  }

  protected firstUpdated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.firstUpdated(changedProperties);
    this.syncSlottedAnchors();
    this.openPendingDownloadIfNeeded();
  }

  protected updated(changedProperties: Map<string | number | symbol, unknown>): void {
    super.updated(changedProperties);
    if (changedProperties.has('downloadUrl')) {
      this.syncSlottedAnchors();
    }

    if (
      changedProperties.has('isAuthenticatedUser') ||
      changedProperties.has('isInstructor') ||
      changedProperties.has('isVerifiedInstructor') ||
      changedProperties.has('hasResolvedInstructorStatus')
    ) {
      this.openPendingDownloadIfNeeded();
    }
  }

  private getDownloadUrl(): string {
    return this.downloadUrl?.trim() || '';
  }

  private openLoginTab(loginUrl: string): Window | null {
    return window.open(loginUrl, '_blank', 'noopener,noreferrer');
  }

  private ensureStudentViewOptionInCurrentUrl(): void {
    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set('viewOption', 'student');
    this.navigate(currentUrl.toString());
  }

  private savePendingDownloadUrl(downloadUrl: string): void {
    localStorage.setItem(DownloadButton.PENDING_DOWNLOAD_STORAGE_KEY, downloadUrl);
    this.hasHandledPendingDownload = false;
  }

  private consumePendingDownloadUrl(): string {
    const pendingDownloadUrl = localStorage.getItem(DownloadButton.PENDING_DOWNLOAD_STORAGE_KEY)?.trim() || '';

    if (pendingDownloadUrl) {
      localStorage.removeItem(DownloadButton.PENDING_DOWNLOAD_STORAGE_KEY);
    }

    return pendingDownloadUrl;
  }

  private openPendingDownloadIfNeeded(): void {
    if (this.hasHandledPendingDownload) {
      return;
    }

    const pendingDownloadUrl = localStorage.getItem(DownloadButton.PENDING_DOWNLOAD_STORAGE_KEY)?.trim() || '';
    if (!pendingDownloadUrl || !this.isAuthenticatedUser) {
      return;
    }

    if (!this.hasResolvedInstructorStatus) {
      return;
    }

    this.hasHandledPendingDownload = true;
    const downloadUrl = this.consumePendingDownloadUrl();
    if (!this.isVerifiedInstructor || !downloadUrl) {
      if (!this.isInstructor) {
        this.ensureStudentViewOptionInCurrentUrl();
      }
      return;
    }

    this.navigate(downloadUrl);
  }

  public navigate(url: string): string {
    window.location.href = url;
    return url;
  }

  public async confirmLogin(event: Event): Promise<boolean> {
    return showConfirmInstructorModal(event);
  }

  public async handleClick(event: Event): Promise<void> {
    const downloadUrl = this.getDownloadUrl();

    if (this.isVerifiedInstructor) {
      if (!downloadUrl) {
        event.preventDefault();
        return;
      }

      event.preventDefault();
      window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    if (!downloadUrl) {
      event.preventDefault();
      return;
    }

    event.preventDefault();

    if (!(await this.confirmLogin(event))) {
      return;
    }

    this.savePendingDownloadUrl(downloadUrl);

    const loginUrl = getLoginURLWithRedirectUrlToCurrentPage();
    this.openLoginTab(loginUrl);
  }

  renderComp() {
    if (!this.getDownloadUrl()) {
      return nothing;
    }

    return html`
      <div
        class="download-link"
        @click="${this.handleClick}"
      >
        <slot @slotchange="${this.syncSlottedAnchors}">${this.label}</slot>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-download-button': DownloadButton;
  }
}
